import React from 'react';
import styled from 'styled-components';
import theme from '../theme';
import Badge from '../components/Badge';
import { getGradeBadgeVariant } from '../../pages/dashboardHelpers';

const CardWrapper = styled.div`
  background: ${theme.colors.surface};
  border-radius: ${theme.radii.md};
  box-shadow: ${theme.shadows.sm};
  border-top: 3px solid ${({ $accent }) => $accent || theme.colors.primary};
  display: flex;
  flex-direction: column;
  flex: 1;
  transition: all ${theme.transitions.fast};

  &:hover {
    box-shadow: ${theme.shadows.md};
    transform: translateY(-1px);
  }
`;

const CardHeader = styled.div`
  padding: ${theme.spacing.lg} ${theme.spacing.lg} 0;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const TitleArea = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const IconCircle = styled.div`
  width: 36px;
  height: 36px;
  border-radius: ${theme.radii.sm};
  background: ${({ $accent }) => `${$accent}12`};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
`;

const Title = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-weight: 600;
  font-size: ${theme.typography.body.size};
  color: ${theme.colors.text};
`;

const StatsRow = styled.div`
  display: flex;
  justify-content: space-around;
  padding: ${theme.spacing.lg};
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatNumber = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-size: 20px;
  font-weight: 700;
  color: ${({ $color }) => $color || theme.colors.text};
`;

const StatLabel = styled.div`
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.tiny.size};
  color: ${theme.colors.textMuted};
  margin-top: 2px;
`;

const ButtonRow = styled.div`
  padding: 0 ${theme.spacing.lg} ${theme.spacing.md};
`;

const ActionButton = styled.button`
  width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border: none;
  border-radius: ${theme.radii.sm};
  background: ${({ $accent }) => $accent || theme.colors.primary};
  color: white;
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.small.size};
  font-weight: 600;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  opacity: ${({ disabled }) => disabled ? 0.6 : 1};

  &:hover:not(:disabled) {
    filter: brightness(1.1);
  }
`;

const FeedbackToggle = styled.button`
  width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border: none;
  border-top: 1px solid ${theme.colors.borderLight};
  background: transparent;
  color: ${theme.colors.primary};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.small.size};
  font-weight: 600;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  border-radius: 0 0 ${theme.radii.md} ${theme.radii.md};

  &:hover {
    background: ${theme.colors.primaryGhost};
  }
`;

const ICONS = {
  'Messaging Defense': '\uD83C\uDFAF',
  'Objection Handling': '\u2753',
  'Document Defense': '\uD83D\uDCC4',
};

const ACCENT_COLORS = {
  'Messaging Defense': theme.colors.prospectPrep,
  'Objection Handling': theme.colors.qaPractice,
  'Document Defense': theme.colors.dealCoaching,
};

function getStatusBadge(statusText) {
  if (statusText === 'Completed Today') return { variant: 'success', label: statusText };
  if (statusText === 'Completed Yesterday') return { variant: 'warning', label: statusText };
  if (statusText === 'Not Started') return { variant: 'neutral', label: statusText };
  return { variant: 'neutral', label: statusText };
}

const ResponseTrainingCard = ({
  sessionType,
  totalSessions,
  thisMonth,
  statusText,
  userAssessments,
  actionLabel,
  onAction,
  isLoading,
  onViewFeedback,
  isExpanded,
}) => {
  const accent = ACCENT_COLORS[sessionType] || theme.colors.primary;
  const badge = getStatusBadge(statusText);
  const gradeVariant = getGradeBadgeVariant(sessionType, userAssessments || []);

  return (
    <CardWrapper $accent={accent}>
      <CardHeader>
        <TitleArea>
          <IconCircle $accent={accent}>
            {ICONS[sessionType] || '\uD83C\uDFAF'}
          </IconCircle>
          <Title>{sessionType}</Title>
        </TitleArea>
        <Badge $variant={gradeVariant || badge.variant}>{badge.label}</Badge>
      </CardHeader>

      <StatsRow>
        <StatItem>
          <StatNumber $color={accent}>{totalSessions}</StatNumber>
          <StatLabel>Total Sessions</StatLabel>
        </StatItem>
        <StatItem>
          <StatNumber $color={thisMonth > 0 ? accent : theme.colors.textMuted}>
            {thisMonth}
          </StatNumber>
          <StatLabel>This Month</StatLabel>
        </StatItem>
      </StatsRow>

      <ButtonRow>
        <ActionButton
          $accent={accent}
          onClick={onAction}
          disabled={isLoading}
        >
          {isLoading ? 'Loading...' : actionLabel}
        </ActionButton>
      </ButtonRow>

      <FeedbackToggle onClick={onViewFeedback}>
        {isExpanded ? 'Hide' : 'View'} Feedback
      </FeedbackToggle>
    </CardWrapper>
  );
};

export default ResponseTrainingCard;
